import {
  useInvoicesPaymentLinkCreateMutation,
  useInvoicesPaymentLinkListQuery,
} from '@bambi/api-client/bambi';
import type { Invoice } from '@bambi/api-client/schemas';
import { useCallback } from 'react';

import {
  setEditingPaymentLinkId,
  setShowPaymentLinkModal,
} from '../../invoice.slice';
import { useAppDispatch } from 'src/app/store/hooks';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';

type FormData = {
  payerType: 'payer_organization' | 'passenger';
  payerId: string;
};

export function usePaymentLink(invoice: Invoice | null, skipRetrieve = false) {
  const dispatch = useAppDispatch();
  const [createPaymentLink, { isLoading: isCreatingPaymentLink }] =
    useInvoicesPaymentLinkCreateMutation();

  const { isLoading, data } = useInvoicesPaymentLinkListQuery(
    {
      invoice: invoice?.id,
    },
    {
      skip: !invoice || skipRetrieve,
    }
  );

  const handleCreatePaymentLink = useCallback(
    async (formData: FormData) => {
      if (!invoice) {
        return;
      }

      try {
        const result = await createPaymentLink({
          invoicePaymentLinkWritable: {
            invoice: invoice.id,
            by_passenger:
              formData.payerType === 'passenger' ? formData.payerId : null,
            by_payer:
              formData.payerType === 'payer_organization'
                ? formData.payerId
                : null,
          },
        }).unwrap();

        dispatch(setEditingPaymentLinkId(result.id));
        dispatch(setShowPaymentLinkModal(true));
      } catch (e) {
        dispatch(
          show({
            type: 'error',
            title: 'Unable to create payment link',
            description: formatServerError(e),
          })
        );
      }
    },
    [createPaymentLink, dispatch, invoice]
  );

  const handleShowPaymentLink = useCallback(
    (formData: FormData) => {
      if (isLoading) {
        return;
      }

      const lastValidPaymentLink = data?.results.find(
        (link) => !link.is_expired
      );

      // Open existing
      if (lastValidPaymentLink) {
        dispatch(setEditingPaymentLinkId(lastValidPaymentLink.id));
        dispatch(setShowPaymentLinkModal(true));
        return;
      }

      // Create new link
      handleCreatePaymentLink(formData);
    },
    [data?.results, dispatch, handleCreatePaymentLink, isLoading]
  );

  return {
    isLoading: isLoading || isCreatingPaymentLink,
    showPaymentLink: handleShowPaymentLink,
    createPaymentLink: handleCreatePaymentLink,
  };
}
